import { addYears, format, parseISO } from 'date-fns'

import { HotelPricingConfig, HotelPricingRates } from '@monorepo/functions/src/types/config'
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore'

import { firestore } from '../firebase/firebase'

export class NoPricingPeriodError extends Error {
    constructor(date: string) {
        super(`No hay periodo de tarifa configurado para la fecha ${date}`)
        this.name = 'NoPricingPeriodError'
    }
}

export function generatePeriodId(startDate: string, endDate: string): string {
    const startYear = startDate.slice(0, 4)
    const endYear = endDate.slice(0, 4)
    const suffix = Math.random().toString(36).slice(2, 8)
    return `${startYear}-${endYear}-${suffix}`
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

export function isValidIsoDate(date: string): boolean {
    if (!ISO_DATE_REGEX.test(date)) return false
    const parsed = parseISO(date)
    return !Number.isNaN(parsed.getTime()) && format(parsed, 'yyyy-MM-dd') === date
}

export function validatePeriodDateRange(
    startDate: string,
    endDate: string,
): { valid: true } | { valid: false; error: string } {
    if (!isValidIsoDate(startDate) || !isValidIsoDate(endDate)) {
        return { valid: false, error: 'Introduce fechas válidas' }
    }
    if (startDate > endDate) {
        return { valid: false, error: 'La fecha de inicio no puede ser posterior a la de fin' }
    }
    return { valid: true }
}

export function formatPeriodHeadline(startDate: string, endDate: string): string {
    const validation = validatePeriodDateRange(startDate, endDate)
    if (!validation.valid) {
        return 'Periodo con fechas no válidas'
    }
    const start = format(parseISO(startDate), 'd MMM yyyy')
    const end = format(parseISO(endDate), 'd MMM yyyy')
    return `De ${start} a ${end}`
}

export function periodRatesToConfig(period: HotelPricingRates): HotelPricingConfig {
    const { name: _name, startDate: _startDate, endDate: _endDate, ...config } = period
    return config
}

export function periodsOverlap(
    a: { startDate: string; endDate: string },
    b: { startDate: string; endDate: string },
): boolean {
    return a.startDate <= b.endDate && b.startDate <= a.endDate
}

export function findOverlappingPeriod(
    candidate: { startDate: string; endDate: string; id?: string },
    periods: HotelPricingRates[],
): HotelPricingRates | null {
    return periods.find(p => p.id !== candidate.id && periodsOverlap(candidate, p)) ?? null
}

export function resolvePricingForDate(date: string, periods: HotelPricingRates[]): HotelPricingConfig {
    const directMatches = periods.filter(p => date >= p.startDate && date <= p.endDate)
    if (directMatches.length > 1) {
        throw new Error(`Múltiples periodos de tarifa solapan la fecha ${date}`)
    }
    if (directMatches.length === 1) {
        return periodRatesToConfig(directMatches[0])
    }

    const previous = periods
        .filter(p => p.endDate <= date)
        .sort((a, b) => b.endDate.localeCompare(a.endDate))[0]
    if (previous) {
        return periodRatesToConfig(previous)
    }

    throw new NoPricingPeriodError(date)
}

export function getPeriodsForDateRange(
    checkIn: string,
    checkOut: string,
    periods: HotelPricingRates[],
): HotelPricingRates[] {
    return periods.filter(p => p.startDate <= checkOut && p.endDate >= checkIn)
}

export async function loadPeriodsForDateRange(
    checkIn: string,
    checkOut: string,
): Promise<HotelPricingRates[]> {
    const periodsRef = collection(firestore, 'configs', 'hotel_pricing', 'periods')
    const overlappingSnap = await getDocs(
        query(periodsRef, where('startDate', '<=', checkOut), where('endDate', '>=', checkIn)),
    )

    const periodsMap = new Map<string, HotelPricingRates>()
    for (const periodDoc of overlappingSnap.docs) {
        periodsMap.set(periodDoc.id, { id: periodDoc.id, ...periodDoc.data() } as HotelPricingRates)
    }

    const fallbackSnap = await getDocs(
        query(periodsRef, where('endDate', '<', checkIn), orderBy('endDate', 'desc'), limit(1)),
    )
    for (const periodDoc of fallbackSnap.docs) {
        if (!periodsMap.has(periodDoc.id)) {
            periodsMap.set(periodDoc.id, { id: periodDoc.id, ...periodDoc.data() } as HotelPricingRates)
        }
    }

    return Array.from(periodsMap.values()).sort((a, b) => a.startDate.localeCompare(b.startDate))
}

export function shiftPeriodDatesByOneYear(period: Pick<HotelPricingRates, 'startDate' | 'endDate'>) {
    return {
        startDate: format(addYears(parseISO(period.startDate), 1), 'yyyy-MM-dd'),
        endDate: format(addYears(parseISO(period.endDate), 1), 'yyyy-MM-dd'),
    }
}

export function findCurrentPeriod(periods: HotelPricingRates[], today = format(new Date(), 'yyyy-MM-dd')) {
    return periods.find(p => today >= p.startDate && today <= p.endDate) ?? null
}

export function getMostRecentPeriod(periods: HotelPricingRates[]): HotelPricingRates | null {
    if (!periods.length) return null
    return [...periods].sort((a, b) => b.endDate.localeCompare(a.endDate))[0]
}
